import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import { UserRole } from '../../lib/types/auth';
import { cn } from '../../utils/cn';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import { SidebarItem, SidebarConfig, ICON_MAP, LayoutProfile } from '../../lib/types/sidebar';
import { ProfileImage } from '../ui/ProfileImage';
import { isPathExcluded } from '../../lib/access/pageExclusions';
const finLogo = '/FINLOGO.png';

interface SimplePageListProps {
  profile: LayoutProfile;
  isMobile?: boolean;
  isMobileOpen?: boolean;
  onClose?: () => void;
}

export const SimplePageList: React.FC<SimplePageListProps> = ({
  profile,
  isMobile = false,
  isMobileOpen = true,
  onClose,
}) => {
  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>([]);
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser, logout } = useAuth();
  const location = useLocation();

  // Memoize stable keys for dependencies
  const profileId = profile.id;
  const customItemsCount = profile.customSidebarItems?.length || 0;
  const userRolesKey = useMemo(() => currentUser?.roles?.join(',') || '', [currentUser?.roles]);

  // Roles that have access to ALL pages regardless of individual page settings
  const FULL_ACCESS_ROLES: UserRole[] = [
    UserRole.MasterAdmin,
    UserRole.Owner,
    UserRole.AllAdmin,
  ];

  const hasAccess = useCallback((roles: UserRole[]): boolean => {
    if (!currentUser?.roles || !Array.isArray(currentUser.roles) || !Array.isArray(roles))
      return false;
    
    // Full access roles can see everything
    if (currentUser.roles.some(role => FULL_ACCESS_ROLES.includes(role))) {
      return true;
    }
    
    return roles.length === 0 || currentUser.roles.some((role) => roles.includes(role));
  }, [userRolesKey]);

  // Use profile's custom items if available, otherwise fetch from main sidebar config
  useEffect(() => {
    // If profile has custom sidebar items, use those
    if (profile.customSidebarItems && profile.customSidebarItems.length > 0) {
      setSidebarItems(profile.customSidebarItems);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch from main sidebar config and flatten to accessible pages
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'sidebar_config'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as SidebarConfig;
          if (data.items && Array.isArray(data.items)) {
            // Flatten all items and filter to only links the user has access to
            const flattenItems = (items: SidebarItem[], userRoles: UserRole[]): SidebarItem[] => {
              return items.reduce((acc: SidebarItem[], item) => {
                const itemHasAccess = item.roles.length === 0 || userRoles.some(role => item.roles.includes(role));
                if (item.type === 'link' && !item.hidden && itemHasAccess) {
                  acc.push(item);
                }
                if (item.type === 'folder' && item.children) {
                  acc.push(...flattenItems(item.children, userRoles));
                }
                return acc;
              }, []);
            };

            const accessiblePages = flattenItems(data.items, currentUser?.roles || []);
            accessiblePages.sort((a, b) => a.order - b.order);
            setSidebarItems(accessiblePages);
          }
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading pages:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [profileId, customItemsCount, userRolesKey]); // Use stable primitive values

  const isActive = (path?: string): boolean => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleFolder = (folderId: string) => {
    setOpenFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const renderIcon = (iconName?: string) => {
    if (!iconName) return null;
    
    let IconComponent = (Icons as any)[iconName];
    
    if (!IconComponent) {
      const mappedName = ICON_MAP[iconName.toLowerCase()];
      IconComponent = mappedName ? (Icons as any)[mappedName] : null;
    }
    
    if (!IconComponent) {
      IconComponent = Icons.Circle;
    }
    
    return <IconComponent size={20} strokeWidth={1.5} />;
  };

  const renderSidebarItem = (item: SidebarItem, level = 0) => {
    if (!hasAccess(item.roles)) return null;
    // Per-user page exclusion overrides role access (incl. full-access roles).
    if (item.type === 'link' && isPathExcluded(item.path, currentUser?.excludedPages)) return null;

    const paddingLeft = level * 12 + 16;

    if (item.type === 'folder') {
      const isOpen = openFolders[item.id];
      const hasVisibleChildren = item.children?.some(child => hasAccess(child.roles));
      
      if (!hasVisibleChildren) return null;

      return (
        <div key={item.id}>
          <button
            onClick={() => toggleFolder(item.id)}
            className={cn(
              'w-full flex items-center gap-3 py-3 rounded-lg transition-all duration-200',
              'text-gray-700 hover:text-gray-900 hover:bg-blue-100/70'
            )}
            style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px' }}
          >
            <span className="text-gray-500">
              {renderIcon(item.icon)}
            </span>
            <span className="font-medium flex-1 text-left">{item.name}</span>
            <motion.span
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <Icons.ChevronDown size={16} className="text-gray-400" />
            </motion.span>
          </button>
          
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                {item.children
                  ?.sort((a, b) => a.order - b.order)
                  .map(child => renderSidebarItem(child, level + 1))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    // Regular link
    return (
      <NavLink
        key={item.id}
        to={item.path}
        onClick={() => isMobile && onClose?.()}
        className={({ isActive: navActive }) =>
          cn(
            'flex items-center gap-3 py-3 rounded-lg transition-all duration-200',
            'text-gray-700 hover:text-gray-900 hover:bg-blue-100/70',
            (navActive || isActive(item.path)) &&
              'bg-blue-50 text-blue-900 border-l-[3px] border-blue-700 rounded-l-none'
          )
        }
        style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px' }}
      >
        <span className="text-gray-500">
          {renderIcon(item.icon)}
        </span>
        <span className="font-medium">{item.name}</span>
      </NavLink>
    );
  };

  const userInfo = {
    name: currentUser?.displayName || 'User',
    email: currentUser?.email || '',
    role: currentUser?.roles?.[0] || UserRole.StoreStaff,
    photoURL: currentUser?.photoURL || null,
  };

  if (isMobile && !isMobileOpen) {
    return null;
  }

  return (
    <>
      {isMobile && isMobileOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onClose}
        />
      )}

      <motion.aside
        initial={isMobile ? { x: '-100%' } : false}
        animate={{ x: 0 }}
        exit={isMobile ? { x: '-100%' } : undefined}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={cn(
          'h-full bg-neutral-50',
          'border-r border-gray-200/80 flex flex-col shadow-sm',
          'w-[280px]',
          isMobile && 'fixed left-0 top-0 z-50 shadow-2xl'
        )}
      >
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200/90">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={finLogo} alt="FIN" className="h-8 object-contain" />
            </div>
            {isMobile && (
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-800 transition-colors"
              >
                <Icons.X size={20} />
              </button>
            )}
          </div>
          <div className="mt-3 px-1">
            <span className="text-xs text-emerald-700/90 font-medium tracking-wider uppercase">
              {profile.name}
            </span>
          </div>
        </div>

        {/* Page List */}
        <nav className="flex-1 overflow-y-auto py-4 custom-scrollbar">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-emerald-400" />
            </div>
          ) : sidebarItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-600">
              <Icons.FileX className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p className="text-sm">No pages configured</p>
            </div>
          ) : (
            <div className="space-y-1 px-2">
              {sidebarItems
                .sort((a, b) => a.order - b.order)
                .map(item => renderSidebarItem(item))}
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="border-t border-gray-200/90 p-4">
          <div className="flex items-center gap-3 mb-4">
            <ProfileImage
              photoURL={userInfo.photoURL}
              displayName={userInfo.name}
              size="md"
              className="ring-2 ring-gray-200/80"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {userInfo.name}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {userInfo.role}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <NavLink
              to="/profile"
              className="flex-1"
              onClick={() => isMobile && onClose?.()}
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full border-gray-200 hover:bg-gray-100/90 text-gray-800"
              >
                <Icons.User size={16} className="mr-2" />
                Profile
              </Button>
            </NavLink>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
            >
              <Icons.LogOut size={16} />
            </Button>
          </div>
        </div>
      </motion.aside>
    </>
  );
};
